import languages from "@/data/languages.json";

type BlogLanguageInput = {
  slug?: string;
  title?: string;
  description?: string;
  lang?: string;
};

const LOCALE_LABELS = new Set([
  "English",
  "Spanish",
  "German",
  "Italian",
  "French",
  "Dutch",
  "Danish",
  "Swedish",
  "Norwegian",
  "Finnish",
  "Russian",
  "Turkish",
  "Arabic",
  "Japanese",
  "Korean",
  "Mandarin",
  "Mandarin Chinese",
  "Chinese",
]);

const GENERIC_LANGUAGE_LABELS = new Set(["English", "All Languages"]);

const LOCALE_CODE_TO_LANGUAGE: Record<string, string> = {
  ar: "Arabic",
  da: "Danish",
  de: "German",
  en: "English",
  es: "Spanish",
  fi: "Finnish",
  fr: "French",
  it: "Italian",
  ja: "Japanese",
  ko: "Korean",
  nl: "Dutch",
  no: "Norwegian",
  ru: "Russian",
  sv: "Swedish",
  tr: "Turkish",
  zh: "Mandarin Chinese",
};

function cleanLanguageName(value: string): string {
  return value
    .replace(/\([^)]*\)/g, "")
    .replace(/^Latin American\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const languageCandidates = Array.from(
  new Set([
    ...Object.keys(languages).map(cleanLanguageName),
    "Arabic",
    "Chinese",
    "English",
    "Mandarin",
    "Mandarin Chinese",
    "Spanish",
    "Tagalog",
  ])
)
  .map((name) => ({
    label: name === "Chinese" || name === "Mandarin" ? "Mandarin Chinese" : name,
    normalized: normalizeText(name),
  }))
  .filter((entry) => entry.normalized.length > 0)
  .sort((a, b) => b.normalized.length - a.normalized.length);

function findLanguageInSearchText(searchText: string): string | undefined {
  const normalizedSearch = ` ${normalizeText(searchText)} `;

  for (const candidate of languageCandidates) {
    const needle = ` ${candidate.normalized} `;
    if (normalizedSearch.includes(needle)) {
      return candidate.label;
    }
  }

  return undefined;
}

export function resolveBlogPostLanguage(post: BlogLanguageInput): string {
  const currentLanguage = post.lang?.trim();
  const searchText = [post.title, post.slug, post.description].filter(Boolean).join(" ");
  const inferredLanguage = findLanguageInSearchText(searchText);

  if (inferredLanguage && (!currentLanguage || GENERIC_LANGUAGE_LABELS.has(currentLanguage))) {
    return inferredLanguage;
  }

  if (currentLanguage) {
    return currentLanguage;
  }

  return inferredLanguage ?? "All Languages";
}

export function formatBlogPostCtaLabel(post: BlogLanguageInput): string {
  const language = resolveBlogPostLanguage(post);
  if (language === "All Languages") {
    return "Try ChickyTutor free";
  }

  return `Try ChickyTutor for ${language}`;
}

export function normalizeBlogPostLanguageLabel(language: string | null | undefined): string {
  if (!language) return "";

  const trimmed = language.trim();
  if (!trimmed) return "";

  const normalized = normalizeText(trimmed);
  const localeMatchByCode = LOCALE_CODE_TO_LANGUAGE[normalized];
  if (localeMatchByCode) return localeMatchByCode;

  const localeMatch = languageCandidates.find((candidate) => candidate.normalized === normalized);
  if (localeMatch) return localeMatch.label;

  const cleaned = cleanLanguageName(trimmed);
  const cleanedMatch = languageCandidates.find(
    (candidate) => candidate.normalized === normalizeText(cleaned)
  );
  if (cleanedMatch) return cleanedMatch.label;

  if (LOCALE_LABELS.has(trimmed)) return trimmed;

  return trimmed;
}
